import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function PaymentPaypalSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [status, setStatus] = useState("pending"); // pending | ok | error
  const [message, setMessage] = useState("Confirming your subscription with PayPal…");
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const subId = params.get("subscription_id");
    if (!subId) {
      setStatus("error");
      setMessage("No subscription id in the return URL.");
      return;
    }
    (async () => {
      try {
        const { data } = await api.post("/payments/paypal/activate", { subscription_id: subId });
        setStatus("ok");
        setMessage(`You're now a Premium ${data.plan || ""} member. Welcome! 💛`);
        if (checkAuth) await checkAuth();
      } catch (e) {
        setStatus("error");
        setMessage(e?.response?.data?.detail || "We couldn't verify your subscription. If you were charged, contact support.");
      }
    })();
  }, [params, checkAuth]);

  return (
    <div className="min-h-screen bg-[#FFFDF9] dark:bg-[#1A1615]">
      <Navbar />
      <main className="max-w-xl mx-auto px-4 py-20 text-center" data-testid="payment-paypal-success-page">
        {status === "pending" && (
          <>
            <Loader2 className="w-12 h-12 mx-auto animate-spin text-[#C24C29]" />
            <h1 className="font-serif text-3xl font-bold mt-6 text-[#2C2623] dark:text-[#FAF5F0]">Almost there…</h1>
            <p className="mt-3 text-[#6E655F] dark:text-[#B5AAA1]">{message}</p>
          </>
        )}
        {status === "ok" && (
          <>
            <CheckCircle2 className="w-14 h-14 mx-auto text-green-600" />
            <h1 className="font-serif text-3xl font-bold mt-6 text-[#2C2623] dark:text-[#FAF5F0]">Welcome to Premium</h1>
            <p className="mt-3 text-[#6E655F] dark:text-[#B5AAA1]">{message}</p>
            <button
              data-testid="paypal-success-continue"
              onClick={() => navigate("/discover")}
              className="mt-8 px-6 py-3 rounded-full bg-[#C24C29] text-white font-semibold hover:bg-[#A8411F] transition-colors"
            >
              Continue to Discover
            </button>
          </>
        )}
        {status === "error" && (
          <>
            <XCircle className="w-14 h-14 mx-auto text-red-600" />
            <h1 className="font-serif text-3xl font-bold mt-6 text-[#2C2623] dark:text-[#FAF5F0]">Something didn't line up</h1>
            <p className="mt-3 text-[#6E655F] dark:text-[#B5AAA1]">{message}</p>
            <button
              data-testid="paypal-success-retry"
              onClick={() => navigate("/premium")}
              className="mt-8 px-6 py-3 rounded-full bg-[#C24C29] text-white font-semibold hover:bg-[#A8411F] transition-colors"
            >
              Back to Premium
            </button>
          </>
        )}
      </main>
    </div>
  );
}
